/**
 * MFA Data Preparation Service
 *
 * Handles MFA setup data preparation and storage.
 * Single responsibility: MFA data preparation and storage.
 */

import { createLogger } from '@utils/Logger'
import { DataStore } from '@utils/DataStore'
import { getDefaultMfaFactors } from '@utils/mfaUtils'
import { saveRegistrationContext } from '@utils/registrationContext'

const logger = createLogger('MfaDataService')

/**
 * MFA setup data structure for session storage
 */
export interface MfaSetupData {
  mfaFactors: any[]
  mfaRequired: boolean
}

/**
 * Prepare MFA enrollment data and store it in session storage and registration context
 *
 * @param stateToken - Optional state token from authentication flow
 * @returns true if successful, false otherwise
 */
export const prepareMfaEnrollmentData = (stateToken?: string): boolean => {
  try {
    const defaultFactors = getDefaultMfaFactors()

    // Store MFA factors in session storage for legacy compatibility
    const mfaSetupData: MfaSetupData = {
      mfaFactors: defaultFactors,
      mfaRequired: true,
    }

    sessionStorage.setItem('mfaSetupData', JSON.stringify(mfaSetupData))

    // Store state token in DataStore for legacy compatibility
    if (stateToken) {
      DataStore.setItem('stateToken', stateToken)
    }

    // Store in registration context for new approach
    if (stateToken) {
      const success = saveRegistrationContext({
        stateToken,
        mfaFactors: defaultFactors,
      })

      if (!success) {
        logger.warn('Failed to save registration context for MFA enrollment')
      }
    }

    return true
  } catch (error) {
    logger.error('Failed to prepare MFA enrollment data:', error)
    return false
  }
}

/**
 * Prepare MFA verification data for users with existing MFA enrollment
 *
 * @param stateToken - Optional state token from authentication flow
 * @returns true if successful, false otherwise
 */
export const prepareMfaVerificationData = (stateToken?: string): boolean => {
  try {
    // For MFA verification, we don't need to store setup data
    // The registration context should already be created by AuthService

    if (stateToken) {
      // Store state token in DataStore for legacy compatibility
      DataStore.setItem('stateToken', stateToken)
    }


    return true
  } catch (error) {
    logger.error('Failed to prepare MFA verification data:', error)
    return false
  }
}

/**
 * Store MFA setup data to session storage and registration context
 *
 * @param mfaFactors - MFA factors to store
 * @param stateToken - Optional state token
 * @returns true if successful, false otherwise
 */
export const storeMfaSetupData = (mfaFactors: any[], stateToken?: string): boolean => {
  try {
    // Store in session storage
    const mfaSetupData: MfaSetupData = {
      mfaFactors,
      mfaRequired: true,
    }

    sessionStorage.setItem('mfaSetupData', JSON.stringify(mfaSetupData))

    // Store state token if provided
    if (stateToken) {
      DataStore.setItem('stateToken', stateToken)
    }

    // Store in registration context
    if (stateToken) {
      const success = saveRegistrationContext({
        stateToken,
        mfaFactors,
      })

      if (!success) {
        logger.warn('Failed to save registration context for MFA setup')
      }
    }

    logger.debug('MFA setup data stored successfully', {
      factorCount: mfaFactors.length,
      hasStateToken: !!stateToken,
    })

    return true
  } catch (error) {
    logger.error('Failed to store MFA setup data:', error)
    return false
  }
}

/**
 * Clear MFA setup data from session storage
 *
 * @returns true if successful, false otherwise
 */
export const clearMfaSetupData = (): boolean => {
  try {
    sessionStorage.removeItem('mfaSetupData')
    logger.debug('MFA setup data cleared from session storage')
    return true
  } catch (error) {
    logger.error('Failed to clear MFA setup data:', error)
    return false
  }
}

/**
 * Get MFA setup data from session storage
 *
 * @returns MFA setup data or null if not found
 */
export const getMfaSetupData = (): MfaSetupData | null => {
  try {
    const data = sessionStorage.getItem('mfaSetupData')
    if (!data) {
      return null
    }

    return JSON.parse(data) as MfaSetupData
  } catch (error) {
    logger.error('Failed to get MFA setup data:', error)
    return null
  }
}
